import { BaseUnit } from '@domain/protocols/Unit'

import { formatNumber } from './number'
import { isNotSet } from './validation'

/**
 * Limites máximos aceitos nos campos numéricos de Peso & Balanceamento e de performance da aeronave.
 *
 * São valores fixos por grandeza: não variam com a unidade escolhida no campo (kg/lb, in/cm/m), o que
 * mantém o limite estável quando o usuário alterna a unidade. Todos terminam em 9 de propósito — assim
 * o limite equivale a uma quantidade de dígitos, e o bloqueio da digitação fica previsível.
 */
export const NUMERIC_FIELD_LIMITS = {
  /** Pesos em geral: básico vazio, máximos, estações, peso predefinido, capacidade de carga */
  weight: 999999,
  /** Braço, LEMAC e MAC Length */
  arm: 99999,
  /** Momento, já dividido pelo índice escolhido no perfil */
  moment: 999999,
  /** Posição do CG em % da corda média aerodinâmica */
  macPercent: 999,
  /** Quantidade de combustível: capacidade do tanque, abastecimento, táxi e voo */
  fuelQuantity: 999999,
  /** Velocidades de subida, cruzeiro e descida */
  speed: 9999,
  /** Razão de subida e de descida */
  rate: 99999,
  /** Consumo por hora */
  fuelConsumption: 99999,
  /** Teto operacional */
  altitude: 99999
} as const

/**
 * Limite de cada grandeza pela unidade-base do campo, para componentes genéricos que só conhecem a
 * `BaseUnit` — como o `UnitField` dentro do `ModalPresenter`.
 */
export const BASE_UNIT_NUMERIC_LIMITS: Record<BaseUnit, number> = {
  speed: NUMERIC_FIELD_LIMITS.speed,
  // Único limite semântico em vez de contagem de dígitos: um rumo não passa de 360°.
  direction: 360,
  altitude: NUMERIC_FIELD_LIMITS.altitude,
  rate: NUMERIC_FIELD_LIMITS.rate,
  fuelConsumption: NUMERIC_FIELD_LIMITS.fuelConsumption,
  fuelQuantity: NUMERIC_FIELD_LIMITS.fuelQuantity,
  volume: NUMERIC_FIELD_LIMITS.fuelQuantity,
  distance: 99999,
  weight: NUMERIC_FIELD_LIMITS.weight,
  linearMeasure: NUMERIC_FIELD_LIMITS.arm
}

/**
 * `true` quando o valor ainda cabe no limite. O sinal é ignorado: campos que aceitam negativo (braço,
 * momento, %CMA) usam o mesmo limite para os dois lados.
 *
 * Valor ausente e estados intermediários da digitação (`''`, `'-'`, `'1.'`) passam — o campo ainda não
 * tem um número para comparar, e quem barra o valor final é o bloqueio da digitação ou o submit.
 */
export function isWithinNumericLimit(value: string | number | null | undefined, max: number): boolean {
  if (isNotSet(value)) return true

  const numericValue = typeof value === 'number' ? value : Number(value.replace(',', '.'))
  if (!Number.isFinite(numericValue)) return true

  return Math.abs(numericValue) <= max
}

/** Mensagem de erro dos formulários quando o valor ultrapassa o limite. */
// TODO: i18n
export function numericLimitMessage(max: number): string {
  return `Valor máximo: ${formatNumber(max, 0)}`
}
